import type { ConnectionStateInterface } from 'store/connections/types';
import type { SettingsInterface } from 'store/settings/types';
import type { UnitReducerInterface } from 'store/units/reducer';
import type { DocumentMetadataInterface } from 'store/documentMetadata/types';
import type { FileStateInterface } from 'store/fileState/types';

export enum StorageUnitType {
  File = 'file',
  Folder = 'folder',
}

export interface ProjectMetadata {
  opportunityNumber?: string;
  country?: string;
}

export type ApplicationType = 'mining' | 'aggregate';

export const APPLICATION_OPTIONS: {
  value: ApplicationType;
  label: string;
}[] = [
  { value: 'mining', label: 'Mining' },
  { value: 'aggregate', label: 'Aggregate' },
];

export interface RiskAssessmentSimulationSettings {
  simulatePeakCapacity: boolean;
  simulatePSD: boolean;
}

/**
 * Simplified simulation guarantees format for risk assessment.
 * Maps from RiskAssessmentSimulationSettings to a more concise format.
 */
export interface SimulationGuarantees {
  peakCapacity: boolean;
  psd: boolean;
}

export interface WorkflowMetadata extends ProjectMetadata {
  projectType?: string | null;
  application?: ApplicationType;
  svedalaTest?: boolean;
  wiReportNumber?: string;
  ai?: number;
  wi?: number;
  riskAssessmentSimulationSettings?: RiskAssessmentSimulationSettings;
  customConnectionColor?: string;
}

export interface StorageUnitIdTypeAndName {
  id: string;
  name: string;
  type: StorageUnitType;
}

export interface StorageUnit {
  id: string;
  name: string;
  type: StorageUnitType;
  parentId?: string;
  fileData?: string;
  thumbnail?: string;
  thumbnailDark?: string;
  createdAt: Date;
  updatedAt?: Date;
  exclusiveLease?: Lease | null;
  softLeases: Lease[];
  access: AccessDatabaseUser;
  status: StorageUnitStatus[];
  archived?: boolean;
  project?: ProjectMetadata;
  flowsheetMetadata?: WorkflowMetadata;
  /**
   * Client-side metadata for offline support
   */
  isSynced?: boolean;
  syncedAt?: string | null;
  localUpdatedAt?: string | null;
  pendingAction?: 'create' | 'update' | 'archive' | null;
  pendingActionBeforeArchive?: 'update' | null;
}

export interface AccessDatabaseUser {
  direct: string[];
  indirect: string[];
}

export interface StorageUnitCreate {
  parentId?: string;
  name: string;
  type: StorageUnitType;
  fileData?: string;
  thumbnail?: string;
  thumbnailDark?: string;
  status?: StorageUnitStatus[];
  projectMetadata?: ProjectMetadata;
  flowsheetMetadata?: WorkflowMetadata;
}

export interface StorageUnitUpdate {
  id: string;
  parentId?: string;
  name?: string;
  type: StorageUnitType;
  fileData?: string;
  thumbnail?: string;
  thumbnailDark?: string;
  projectMetadata?: ProjectMetadata;
  flowsheetMetadata?: WorkflowMetadata;
}

export interface Status {
  storageUnitId: string;
  status: string;
}

export enum LeaseType {
  Exclusive = 'EXCLUSIVE',
  Soft = 'SOFT',
}

export interface Lease {
  userId: string;
  userName: string;
  leasedAt: Date;
  leaseType: LeaseType;
}

export enum LeaseAction {
  Acquire,
  Release,
}

export interface FileState {
  unitState: UnitReducerInterface;
  connectionState: ConnectionStateInterface;
  settingState: SettingsInterface & { fileName: string };
  documentMetadata?: DocumentMetadataInterface;
  fileState?: FileStateInterface;
  dataVersion?: number;
  clearApprovedStatus?: boolean;
}

export interface KeycloakUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface AccessCreateRequest {
  emails: string[];
}

export interface AccessDelete {
  id: string;
  email: string;
}

export interface AccessRead {
  direct: KeycloakUser[];
  indirect: KeycloakUser[];
}

export const ROOT = {
  id: 'root',
  name: 'All files',
  type: StorageUnitType.Folder,
};

export enum UpdateActionOrigin {
  DESIGNER = 'designer',
  AUTOSAVE = 'autosave',
  RENAME_MODAL = 'rename-modal',
  MOVE_MODAL = 'move-modal',
  DRAG_AND_DROP = 'drag-and-drop',
  UNDO = 'undo',
  THUMBNAIL = 'thumbnail',
}

export enum SortType {
  ALPHABETICAL = 'ALPHABETICAL',
  LAST_MODIFIED = 'LAST_MODIFIED',
}

export enum StorageUnitStatus {
  APPROVED = 'approved',
  TEMPLATE = 'template',
}

export const CREATE_STORAGE_UNIT_REQUEST = 'CREATE_STORAGE_UNIT_REQUEST';

export const GET_STORAGE_UNIT_REQUEST = 'GET_STORAGE_UNIT_REQUEST';
export const GET_STORAGE_UNITS_REQUEST = 'GET_STORAGE_UNITS_REQUEST';
export const GET_RECENT_STORAGE_UNITS_REQUEST =
  'GET_RECENT_STORAGE_UNITS_REQUEST';

export const GET_TEMPLATES_REQUEST = 'GET_TEMPLATES_REQUEST';
export const GET_ARCHIVED_REQUEST = 'GET_ARCHIVED_REQUEST';
export const GET_APPROVED_FILES_REQUEST = 'GET_APPROVED_FILES_REQUEST';

export const UPDATE_STORAGE_UNIT_REQUEST = 'UPDATE_STORAGE_UNIT_REQUEST';

export const REMOVE_STORAGE_UNIT_REQUEST = 'REMOVE_STORAGE_UNIT_REQUEST';
export const ARCHIVE_STORAGE_UNIT = 'ARCHIVE_STORAGE_UNIT';
export const DELETE_ALL_ARCHIVED_STORAGE_UNIT =
  'DELETE_ALL_ARCHIVED_STORAGE_UNIT';
export const RESTORE_STORAGE_UNIT = 'RESTORE_STORAGE_UNIT';

export const GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST =
  'GET_ACCESS_INFORMATION_FOR_STORAGE_UNIT_REQUEST';

export const GRANT_ACCESS_REQUEST = 'GRANT_ACCESS_REQUEST';
export const REMOVE_ACCESS_REQUEST = 'REMOVE_ACCESS_REQUEST';

export const LEASE_ON_STORAGE_UNIT_REQUEST = 'LEASE_ON_STORAGE_UNIT_REQUEST';
export const LEASE_ON_STORAGE_UNIT_SUCCESS = 'LEASE_ON_STORAGE_UNIT_SUCCESS';
export const POLL_STORAGE_UNIT_LEASES_REQUEST =
  'POLL_STORAGE_UNIT_LEASES_REQUEST';

export enum ExplorerMode {
  List = 'list',
  Grid = 'grid',
}

export enum RequestStatus {
  IDLE = 'IDLE',
  REQUESTED = 'REQUESTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export enum Filter {
  ALL = 'ALL',
  LOCKED = 'LOCKED',
  UNLOCKED = 'UNLOCKED',
  DOWNLOADED = 'DOWNLOADED',
  CLOUD = 'CLOUD',
  LAST_MODIFIED = 'LAST_MODIFIED',
  DRAFT = 'DRAFT',
  APPROVED = 'APPROVED',
  SHARED = 'SHARED',
  PRIVATE = 'PRIVATE',
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface AdvancedFilters {
  country?: string;
  application?: ApplicationType;
}

export type Sort = {
  direction: SortDirection;
  type: SortType;
};

export enum FileUploadStatus {
  UPLOADING = 'UPLOADING',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
}

export type FileUpload = {
  id: string;
  name: string;
  status: FileUploadStatus;
  errorText?: string;
};

export type CurrentFile = Pick<
  StorageUnit,
  | 'id'
  | 'name'
  | 'status'
  | 'updatedAt'
  | 'isSynced'
  | 'syncedAt'
  | 'exclusiveLease'
  | 'softLeases'
  | 'access'
>;

export interface StorageUnitsReducerInterface {
  approvedFiles: StorageUnit[];
  recentFiles: StorageUnit[];
  templates: StorageUnit[];
  storageUnits: StorageUnit[];
  archived: StorageUnit[];
  folderPath: StorageUnit[];
  resolvedStorageUnitIds: Record<string, string>;
  currentFile: CurrentFile;
  loading: boolean;
  directAccess: boolean;
  shareModal: {
    accessInformation?: AccessRead;
    loading: boolean;
  };
  searchString: string;
  members: {
    accessInformation?: AccessRead;
    loading: boolean;
  };
  moreDataToRetrieve: boolean;
  fetchedCount: number;
  requestStatus: RequestStatus;
  sort: Sort;
  filters: Filter[];
  advancedFilters: AdvancedFilters;
  currentUploads: FileUpload[];
}
